import type { ChangeEvent } from 'react';

// Section selection, ordering and reviewer notes.
//
// Ordering is done with Move up / Move down BUTTONS, not drag-and-drop. A
// drag-only reorder is not operable from a keyboard, and Section 508 is a hard
// requirement on this surface.

interface SectionDef {
  key: string;
  label: string;
  default: boolean;
  grant: string;
}

interface ReportSectionsProps {
  sections: SectionDef[];
  chosen: string[];
  order: string[];
  notes: Record<string, string>;
  maxNoteChars: number;
  onToggle: (key: string) => void;
  onMove: (key: string, delta: number) => void;
  onNote: (key: string, value: string) => void;
}

export function ReportSections({
  sections,
  chosen,
  order,
  notes,
  maxNoteChars,
  onToggle,
  onMove,
  onNote,
}: ReportSectionsProps) {
  const rank = (key: string) => {
    const n = order.indexOf(key);
    return n === -1 ? Number.MAX_SAFE_INTEGER : n;
  };
  const selected = sections
    .filter((s) => chosen.includes(s.key))
    .sort((a, b) => rank(a.key) - rank(b.key));
  const rest = sections.filter((s) => !chosen.includes(s.key));

  return (
    <fieldset className="rpt-sections">
      <legend className="rpt-label">Sections</legend>

      {selected.map((s, n) => (
        <div className="rpt-srow" key={s.key}>
          <label className="rpt-check">
            <input
              type="checkbox"
              checked
              onChange={() => onToggle(s.key)}
            />
            <span>{s.label}</span>
          </label>
          <span className="rpt-move">
            <button
              type="button"
              className="ibtn"
              onClick={() => onMove(s.key, -1)}
              disabled={n === 0}
              aria-label={`Move ${s.label} up`}
            >
              ↑
            </button>
            <button
              type="button"
              className="ibtn"
              onClick={() => onMove(s.key, 1)}
              disabled={n === selected.length - 1}
              aria-label={`Move ${s.label} down`}
            >
              ↓
            </button>
          </span>
          <input
            type="text"
            className="rpt-noteinput"
            value={notes[s.key] || ''}
            maxLength={maxNoteChars}
            onChange={(e: ChangeEvent<HTMLInputElement>) => onNote(s.key, e.target.value)}
            aria-label={`Reviewer note for ${s.label}`}
            placeholder="Reviewer note"
          />
        </div>
      ))}

      {rest.map((s) => (
        <div className="rpt-srow rpt-srow-off" key={s.key}>
          <label className="rpt-check">
            <input
              type="checkbox"
              checked={false}
              onChange={() => onToggle(s.key)}
            />
            <span>{s.label}</span>
          </label>
        </div>
      ))}
    </fieldset>
  );
}
